/**
 * StudySync Email Service
 * Handles all email and communication API operations
 */

import { authenticatedApi } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
import type {
  EmailInvitation,
  EmailHistoryRecord,
  ApiResponse,
} from '../types';

export class EmailService {
  /**
   * Send email invitations to participants or collaborators
   */
  static async sendInvitations(
    invitationData: EmailInvitation,
    token: string
  ): Promise<ApiResponse<{ success: boolean; sentCount: number }>> {
    return authenticatedApi.post(
      API_ENDPOINTS.EMAIL.SEND_INVITATIONS,
      invitationData,
      token
    );
  }

  /**
   * Send collaborator invitation emails with pre-filled study information
   */
  static async sendCollaboratorInvitations(
    studyCode: string,
    studyName: string,
    studyPassword: string,
    recipients: string[],
    token: string,
    customSubject?: string,
    customMessage?: string
  ): Promise<ApiResponse<{ success: boolean; sentCount: number }>> {
    const subject = customSubject || `Collaborator Invitation: ${studyName}`;
    // Use custom message if provided, otherwise use default template
    const message = customMessage && customMessage.trim() 
      ? customMessage 
      : this.generateCollaboratorInvitationTemplate(studyName, studyCode, studyPassword);
    
    return this.sendInvitations(
      {
        studyCode,
        studyName,
        participants: recipients.map(email => ({ email })),
        emailSubject: subject,
        emailBody: message,
        invitationType: 'collaborator'
      },
      token
    );
  }

  /**
   * Upload participant roster via CSV
   */
  static async uploadRoster(
    studyCode: string,
    csvFile: File,
    token: string
  ): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    formData.append('studyCode', studyCode);

    return authenticatedApi.post(
      API_ENDPOINTS.EMAIL.UPLOAD_ROSTER,
      formData,
      token,
      {
        headers: {
          // Don't set Content-Type for FormData - let browser set it with boundary
        },
      }
    );
  }

  /**
   * Get email history for a study
   */
  static async getEmailHistory(
    studyId: string,
    token: string
  ): Promise<ApiResponse<EmailHistoryRecord[]>> {
    return authenticatedApi.get<ApiResponse<EmailHistoryRecord[]>>(
      API_ENDPOINTS.EMAIL.HISTORY(studyId),
      token
    );
  }

  /**
   * Validate CSV file format
   */
  static validateCsvFile(file: File): { isValid: boolean; error?: string } {
    // Check file type
    if (!file.type.includes('csv') && !file.name.endsWith('.csv')) {
      return { isValid: false, error: 'File must be a CSV file' };
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { isValid: false, error: 'File size must be less than 5MB' };
    }

    return { isValid: true };
  }

  /**
   * Parse CSV file content (client-side preview)
   */
  static async parseCsvFile(file: File): Promise<{
    headers: string[];
    rows: string[][];
    preview: string[][];
  }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length === 0) {
            reject(new Error('CSV file is empty'));
            return;
          }

          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          const rows = lines.slice(1).map(line => 
            line.split(',').map(cell => cell.trim().replace(/"/g, ''))
          );

          // Get first 5 rows for preview
          const preview = rows.slice(0, 5);

          resolve({ headers, rows, preview });
        } catch (error) {
          reject(new Error('Failed to parse CSV file'));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read CSV file'));
      reader.readAsText(file);
    });
  }

  /**
   * Generate collaborator invitation email template
   */
  static generateCollaboratorInvitationTemplate(
    studyName: string,
    studyCode: string,
    studyPassword: string
  ): string {
    return `Dear Colleague,

You have been invited to collaborate on the research study: ${studyName}

Study Code: ${studyCode}
Study Password: ${studyPassword}

To join this study as a collaborator, please go to the research dashboard and use the "Join Study" option with the study code and password provided above.

Best regards,
The Research Team`;
  }

  /**
   * Generate simple email template for invitations
   */
  static generateSimpleInvitationTemplate(
    studyName: string,
    description: string,
    duration: string,
    studyCode: string
  ): {
    subject: string;
    body: string;
  } {
    const subject = `Research Study Invitation: ${studyName}`;
    
    const body = `Hi participant,

We welcome you to the study of ${studyName}.

${description}${duration ? `

Duration: ${duration}` : ''}

To join the study, use the link: ${process.env.REACT_APP_CLIENT_URL}/participant/enroll
Your study code is: ${studyCode}

Best regards,
The Research Team`;

    return { subject, body };
  }

  /**
   * Generate email template for invitations with study details (legacy)
   */
  static generateInvitationTemplate(
    studyName: string, 
    studyCode: string, 
    studyDescription?: string, 
    studyDuration?: string,
    contactEmail?: string
  ): {
    subject: string;
    body: string;
  } {
    const subject = `Research Study Invitation: ${studyName}`;
    
    const body = `
═══════════════════════════════════════════════════════════
                    RESEARCH STUDY INVITATION
═══════════════════════════════════════════════════════════

Study Name: ${studyName}

${studyDescription ? `Study Description:
${studyDescription}

` : ''}${studyDuration ? `Study Duration: ${studyDuration}

` : ''}Dear Participant,

You are cordially invited to participate in our research study. This study involves the secure collection of health data through Fitbit devices to advance medical research.

HOW TO PARTICIPATE:
1. Visit: ${window.location.origin}/participant/enroll
2. Enter your study code: ${studyCode}
3. Connect your Fitbit device
4. Complete the enrollment process

PRIVACY & SECURITY:
• Your participation is completely voluntary
• All data is encrypted and stored securely
• Your privacy is our top priority
• You can withdraw at any time

CONTACT INFORMATION:
${contactEmail ? `Research Team Email: ${contactEmail}` : 'For questions, please contact the research team'}

═══════════════════════════════════════════════════════════
Study Code: ${studyCode}
═══════════════════════════════════════════════════════════

Thank you for considering participation in our research.

Best regards,
The Research Team`;

    return { subject, body };
  }

  /**
   * Validate email addresses
   */
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number format
   * Accepts both old format (+1 (555) 123-4567) and new format ((111) - 0000 - 000)
   */
  static validatePhoneNumber(phone: string): boolean {
    if (!phone) return true; // Phone is optional
    
    // Extract digits only for validation
    const digits = phone.replace(/\D/g, '');
    
    // Must have exactly 10 digits
    return digits.length === 10;
  }

  /**
   * Validate participant data for invitations
   */
  static validateParticipantData(participants: any[]): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!participants || participants.length === 0) {
      errors.push('At least one participant is required');
      return { isValid: false, errors };
    }

    participants.forEach((participant, index) => {
      if (!participant.email) {
        errors.push(`Participant ${index + 1}: Email is required`);
      } else if (!this.validateEmail(participant.email)) {
        errors.push(`Participant ${index + 1}: Invalid email format`);
      }

      if (participant.phoneNumber && !this.validatePhoneNumber(participant.phoneNumber)) {
        errors.push(`Participant ${index + 1}: Invalid phone number format`);
      }

      if (participant.firstName && participant.firstName.length > 100) {
        errors.push(`Participant ${index + 1}: First name is too long (max 100 characters)`);
      }

      if (participant.lastName && participant.lastName.length > 100) {
        errors.push(`Participant ${index + 1}: Last name is too long (max 100 characters)`);
      }
    });

    return { isValid: errors.length === 0, errors };
  }
}

export default EmailService;
